import CONFIG from "config/config";

import {
  listPurRecords,
  getPurRecord,
  PurClientConfig,
  PurPage,
  PurRecord,
  PurResource,
} from "react-lib/apis/PUR/purApi";
import {
  getPurDocument,
  listPurDocumentLines,
  listPurDocuments,
  PurDocument,
  PurDocumentLinePage,
  PurDocumentPage,
  PurDocumentType,
} from "react-lib/apis/PUR/purDocumentsApi";
import { listPurLookup, PurLookupKind, PurLookupOption } from "react-lib/apis/PUR/purLookupsApi";
import { listPurProductStock, PurProductStockRow } from "react-lib/apis/PUR/purStockApi";

import { purErrorMessageId } from "./purPresentation";

/**
 * MeERP follows the HIS card convention: components dispatch events through
 * `props.onEvent({ message, params })`, MainHISController routes them here, and
 * results flow back down as the `MeERP` state slice. Read screens only; the
 * write flows (drafts, receipts, approvals, verification) still call the PUR
 * adapters directly with a `client` config.
 */

export type OverviewKey = "requests" | "orders" | "receipts" | "documents";
export interface OverviewEntry { count: number | null; errorId: string | null }

export interface RequestState { requestKey: number; loading: boolean; errorId: string | null }
export interface OverviewSlice extends RequestState { summary: Record<OverviewKey, OverviewEntry> }
export interface PageSlice<T> extends RequestState { page: T | null }
export interface RecordDetailSlice extends RequestState { record: PurRecord | null }
export interface DocumentDetailSlice extends RequestState { document: PurDocument | null }

export interface MeERPSlice {
  overview: OverviewSlice;
  lists: Record<string, PageSlice<PurPage>>;
  details: Record<string, RecordDetailSlice>;
  documents: PageSlice<PurDocumentPage>;
  documentDetails: Record<string, DocumentDetailSlice>;
  documentLines: Record<string, PageSlice<PurDocumentLinePage>>;
  stock: PageSlice<{ count: number; records: PurProductStockRow[] }>;
  lookups: Record<string, PageSlice<{ count: number; records: PurLookupOption[] }>>;
}

export type State = { MeERP: MeERPSlice };

const emptySummary = (): Record<OverviewKey, OverviewEntry> => ({
  requests: { count: null, errorId: null },
  orders: { count: null, errorId: null },
  receipts: { count: null, errorId: null },
  documents: { count: null, errorId: null },
});

const emptyPageSlice = <T,>(): PageSlice<T> => ({ requestKey: 0, loading: false, errorId: null, page: null });

export const initialMeERPSlice = (): MeERPSlice => ({
  overview: { requestKey: 0, loading: false, errorId: null, summary: emptySummary() },
  lists: {},
  details: {},
  documents: emptyPageSlice(),
  documentDetails: {},
  documentLines: {},
  stock: emptyPageSlice(),
  lookups: {},
});

export const StateInitial: State = { MeERP: initialMeERPSlice() };

export type Event =
  | { message: "GetMeERPOverview"; params: { requestKey: number } }
  | {
      message: "GetPurResourcePage";
      params: { resource: PurResource; limit: number; offset: number; status?: string; requestKey: number };
    }
  | { message: "GetPurRecordDetail"; params: { resource: PurResource; id: number; requestKey: number } }
  | {
      message: "GetPurDocuments";
      params: { limit: number; offset: number; type?: PurDocumentType; verified?: boolean; requestKey: number };
    }
  | { message: "GetPurDocumentDetail"; params: { id: number; requestKey: number } }
  | { message: "GetPurDocumentLines"; params: { id: number; limit: number; offset: number; requestKey: number } }
  | { message: "GetPurProductStock"; params: { productId: number; limit: number; offset: number; requestKey: number } }
  | {
      message: "GetPurLookup";
      params: { slot: string; kind: PurLookupKind; limit: number; offset: number; search: string; requestKey: number };
    };

// Handlers cannot cancel in-flight requests, so every slot stores the key of
// the request it belongs to and a result never overwrites a newer request.
let requestKeyCounter = 0;
export const nextMeERPRequestKey = (): number => {
  requestKeyCounter += 1;
  return requestKeyCounter;
};

export interface HandlerController {
  apiToken?: string;
  getState: () => { MeERP?: MeERPSlice; selectedDivision?: { id?: number | string; name?: string } | null };
  setState: (state: (previous: { MeERP?: MeERPSlice }) => { MeERP: MeERPSlice } | null) => void;
}
type Handler<M extends Event["message"]> = (controller: HandlerController, params: Extract<Event, { message: M }>["params"]) => Promise<void>;

const buildClient = (controller: HandlerController): PurClientConfig => ({
  apiBaseUrl: CONFIG.API_HOST,
  apiToken: controller.apiToken,
  divisionId: controller.getState().selectedDivision?.id,
});

const currentSlice = (controller: HandlerController): MeERPSlice =>
  controller.getState().MeERP || initialMeERPSlice();

const writeSlice = (controller: HandlerController, update: (slice: MeERPSlice) => MeERPSlice): void => {
  controller.setState((previous) => {
    const slice = previous.MeERP || initialMeERPSlice();
    const next = update(slice);
    return next === slice ? null : { MeERP: next };
  });
};

const isStale = (slot: RequestState | undefined, requestKey: number): boolean =>
  (slot?.requestKey || 0) > requestKey;

const withSlot = <T,>(slots: Record<string, T>, key: string, value: T): Record<string, T> => ({
  ...slots,
  [key]: value,
});

export const GetMeERPOverview: Handler<"GetMeERPOverview"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  if (isStale(currentSlice(controller).overview, key)) return;
  writeSlice(controller, (slice) => isStale(slice.overview, key) ? slice : ({ ...slice, overview: { requestKey: key, loading: true, errorId: null, summary: emptySummary() } }));

  const one = async (resource: PurResource, status: string): Promise<number> =>
    (await listPurRecords(client, resource, { limit: 1, offset: 0, status })).count;
  const tasks: Array<[OverviewKey, () => Promise<number>]> = [
    ["requests", () => one("materialRequests", "SUBMITTED")],
    ["orders", async () =>
      (await Promise.all(["APPROVED", "PARTIALLY_RECEIVED", "TO_RECEIVE"].map((status) => one("purchaseOrders", status)))).reduce((sum, count) => sum + count, 0)],
    ["receipts", () => one("receipts", "DRAFT")],
    ["documents", async () => (await listPurDocuments(client, { limit: 1, offset: 0, verified: false })).count],
  ];

  // Each count lands as soon as its own response resolves, like the screen did.
  const writeEntry = (entryKey: OverviewKey, entry: OverviewEntry): void => {
    writeSlice(controller, (slice) => slice.overview.requestKey !== key ? slice : ({
      ...slice,
      overview: { ...slice.overview, summary: { ...slice.overview.summary, [entryKey]: entry } },
    }));
  };
  await Promise.all(tasks.map(async ([entryKey, query]) => {
    try {
      writeEntry(entryKey, { count: await query(), errorId: null });
    } catch (error: unknown) {
      writeEntry(entryKey, { count: null, errorId: purErrorMessageId(error) });
    }
  }));
  writeSlice(controller, (slice) => slice.overview.requestKey !== key ? slice : ({ ...slice, overview: { ...slice.overview, loading: false } }));
};

export const GetPurResourcePage: Handler<"GetPurResourcePage"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const read = (slice: MeERPSlice) => slice.lists[params.resource as string];
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    lists: withSlot(slice.lists, params.resource, { requestKey: key, loading: true, errorId: null, page: null }),
  }));
  let outcome: { errorId: string | null; page: PurPage | null };
  try {
    outcome = { errorId: null, page: await listPurRecords(client, params.resource, { limit: params.limit, offset: params.offset, status: params.status || undefined }) };
  } catch (error: unknown) {
    outcome = { errorId: purErrorMessageId(error), page: null };
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    lists: withSlot(slice.lists, params.resource, { requestKey: key, loading: false, errorId: outcome.errorId, page: outcome.page }),
  }));
};

export const GetPurRecordDetail: Handler<"GetPurRecordDetail"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const slotKey = `${params.resource}:${params.id}`;
  const read = (slice: MeERPSlice) => slice.details[slotKey];
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    details: withSlot(slice.details, slotKey, { requestKey: key, loading: true, errorId: null, record: null }),
  }));
  let record: PurRecord | null = null;
  let errorId: string | null = null;
  try {
    record = await getPurRecord(client, params.resource, params.id);
  } catch (error: unknown) {
    errorId = purErrorMessageId(error);
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    details: withSlot(slice.details, slotKey, { requestKey: key, loading: false, errorId, record }),
  }));
};

export const GetPurDocuments: Handler<"GetPurDocuments"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const read = (slice: MeERPSlice) => slice.documents;
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({ ...slice, documents: { requestKey: key, loading: true, errorId: null, page: null } }));
  let outcome: { errorId: string | null; page: PurDocumentPage | null };
  try {
    outcome = {
      errorId: null,
      page: await listPurDocuments(client, {
        limit: params.limit,
        offset: params.offset,
        type: params.type || undefined,
        verified: params.verified === undefined ? undefined : Boolean(params.verified),
      }),
    };
  } catch (error: unknown) {
    outcome = { errorId: purErrorMessageId(error), page: null };
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({ ...slice, documents: { requestKey: key, loading: false, errorId: outcome.errorId, page: outcome.page } }));
};

export const GetPurDocumentDetail: Handler<"GetPurDocumentDetail"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const slotKey = String(params.id);
  const read = (slice: MeERPSlice) => slice.documentDetails[slotKey];
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    documentDetails: withSlot(slice.documentDetails, slotKey, { requestKey: key, loading: true, errorId: null, document: null }),
  }));
  let document: PurDocument | null = null;
  let errorId: string | null = null;
  try {
    document = await getPurDocument(client, params.id);
  } catch (error: unknown) {
    errorId = purErrorMessageId(error);
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    documentDetails: withSlot(slice.documentDetails, slotKey, { requestKey: key, loading: false, errorId, document }),
  }));
};

export const GetPurDocumentLines: Handler<"GetPurDocumentLines"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const slotKey = String(params.id);
  const read = (slice: MeERPSlice) => slice.documentLines[slotKey];
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    documentLines: withSlot(slice.documentLines, slotKey, { requestKey: key, loading: true, errorId: null, page: null }),
  }));
  let outcome: { errorId: string | null; page: PurDocumentLinePage | null };
  try {
    outcome = { errorId: null, page: await listPurDocumentLines(client, params.id, params.limit, params.offset) };
  } catch (error: unknown) {
    outcome = { errorId: purErrorMessageId(error), page: null };
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    documentLines: withSlot(slice.documentLines, slotKey, { requestKey: key, loading: false, errorId: outcome.errorId, page: outcome.page }),
  }));
};

export const GetPurProductStock: Handler<"GetPurProductStock"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const read = (slice: MeERPSlice) => slice.stock;
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({ ...slice, stock: { requestKey: key, loading: true, errorId: null, page: null } }));
  let outcome: { errorId: string | null; page: { count: number; records: PurProductStockRow[] } | null };
  try {
    outcome = { errorId: null, page: await listPurProductStock(client, { productId: params.productId, limit: params.limit, offset: params.offset }) };
  } catch (error: unknown) {
    outcome = { errorId: purErrorMessageId(error), page: null };
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({ ...slice, stock: { requestKey: key, loading: false, errorId: outcome.errorId, page: outcome.page } }));
};

export const GetPurLookup: Handler<"GetPurLookup"> = async (controller, params) => {
  const client = buildClient(controller);
  const key: number = params.requestKey;
  const slotKey: string = params.slot;
  const read = (slice: MeERPSlice) => slice.lookups[slotKey];
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    lookups: withSlot(slice.lookups, slotKey, { requestKey: key, loading: true, errorId: null, page: null }),
  }));
  let outcome: { errorId: string | null; page: { count: number; records: PurLookupOption[] } | null };
  try {
    outcome = { errorId: null, page: await listPurLookup(client, params.kind, { limit: params.limit, offset: params.offset, search: params.search }) };
  } catch (error: unknown) {
    // Lookups render through their own message catalogue (MEERP_LOOKUP_*).
    outcome = { errorId: purErrorMessageId(error).replace("MEERP_", "MEERP_LOOKUP_"), page: null };
  }
  if (isStale(read(currentSlice(controller)), key)) return;
  writeSlice(controller, (slice) => isStale(read(slice), key) ? slice : ({
    ...slice,
    lookups: withSlot(slice.lookups, slotKey, { requestKey: key, loading: false, errorId: outcome.errorId, page: outcome.page }),
  }));
};
